import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, HostListener, signal, OnInit, OnChanges, SimpleChanges, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { SliderModule } from 'primeng/slider';
import { ExerciseService } from '@foxera/feature/settings/services/exercise.service';
import { BranchService } from '@foxera/feature/settings/services/branch.service';
import { CustomToastService } from '@foxera/shared/services/custom-toast.service';
import { ResponsiveService } from '@foxera/shared/services/responsive.service';
import { FoxeraDrawerFullComponent } from '@foxera/shared/components/foxera-drawer-full/foxera-drawer-full.component';

const TIME_STEP_SECONDS_WARMUP = 5; // 5 sec steps
const TIME_STEP_SECONDS_CARDIO = 60; // 1 min steps
const TIME_MAX_SECONDS_WARMUP = 300; // 5 min
const TIME_MAX_SECONDS_CARDIO = 1800; // 30 min
const REPS_STEP = 1;
const REPS_MAX = 30;

@Component({
  selector: 'app-client-view-exercise',
  standalone: true,
  templateUrl: './client-view-exercise.component.html',
  styleUrls: ['./client-view-exercise.component.css'],
  imports: [
    CommonModule,
    FormsModule,
    SliderModule,
    FoxeraDrawerFullComponent
  ]
})
export class ClientViewExerciseComponent implements OnInit, OnChanges {
  readonly responsiveService = inject(ResponsiveService);
  
  visible = signal<boolean>(true);
  @Input() exerciseId: string = '';
  @Input() exerciseName: string = '';
  @Input() animationPath: string = '';
  @Input() level: string = '';
  @Input() muscles: string[] = [];
  @Input() tags: string[] = [];
  @Input() category: string = '';
  @Input() avgDuration: number = 0;
  @Input() isTimeOrRepsSection: boolean = false;
  @Input() isCardioSection: boolean = false;
  @Input() totalTimeSec: number | null = null;
  @Input() totalReps: number | null = null;
  @Input() isTimeBased: boolean | null = null;
  @Input() availableToVixey?: boolean;
  @Input() showVixeyToggle: boolean = false; // Only show in browse mode
  @Input() branchId?: string;
  @Input() branchName?: string;
  @Output() close = new EventEmitter<void>();
  @Output() vixeyAvailabilityChanged = new EventEmitter<void>(); // Emit when changes are saved
  @Output() totalTimeSecChanged = new EventEmitter<number>();
  @Output() totalRepsChanged = new EventEmitter<number>();

  readonly repsStep = REPS_STEP;
  readonly repsMax = REPS_MAX;
  timeMax: number = TIME_MAX_SECONDS_WARMUP;
  timeStep: number = TIME_STEP_SECONDS_WARMUP;

  timeOrRepsMode: 'time' | 'reps' = 'time';
  tempTimeSeconds: number = 0;
  tempReps: number = 0;

  // Toggle state
  isAvailableToVixey = signal<boolean>(true);
  originalAvailability = signal<boolean>(true);
  hasChanges = signal<boolean>(false);
  isSaving = signal<boolean>(false);
  changesSaved = signal<boolean>(false); // Track if changes were saved during this session
  
  private resolvedBranchId: string | null = null;

  constructor(
    private exerciseService: ExerciseService,
    private branchService: BranchService,
    private toastService: CustomToastService
  ) {}

  ngOnInit(): void {
    // Initialize toggle state from input
    if (this.availableToVixey !== undefined) {
      this.isAvailableToVixey.set(this.availableToVixey);
      this.originalAvailability.set(this.availableToVixey);
    }

    if (this.branchId) {
      this.resolvedBranchId = this.branchId;
      return;
    }

    // Fallback for standalone browse usage without a preset branch
    this.branchService.getBranches().subscribe({
      next: (branches) => {
        if (branches && branches.length > 0) {
          this.resolvedBranchId = branches[0].id;
        }
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isCardioSection']) {
      this.timeMax = this.isCardioSection ? TIME_MAX_SECONDS_CARDIO : TIME_MAX_SECONDS_WARMUP;
      this.timeStep = this.isCardioSection ? TIME_STEP_SECONDS_CARDIO : TIME_STEP_SECONDS_WARMUP;
    }

    if (changes['totalTimeSec'] || changes['totalReps'] || changes['isTimeBased']) {
      if (this.isTimeBased != null) {
        // Backend explicitly tells us which mode applies - this also covers newly added
        // exercises where totalTimeSec/totalReps are both still null.
        this.timeOrRepsMode = this.isTimeBased ? 'time' : 'reps';
      } else {
        // Fallback for exercises without the flag: whichever field currently has a value.
        this.timeOrRepsMode = (this.totalReps != null && this.totalReps > 0 && !this.totalTimeSec)
          ? 'reps'
          : 'time';
      }
      this.tempTimeSeconds = this.totalTimeSec ?? 0;
      this.tempReps = this.totalReps ?? 0;
    }
  }

  onTimeSliderChange(value: number): void {
    this.tempTimeSeconds = value;
    this.totalTimeSecChanged.emit(value);
  }

  /**
   * Formats a duration in seconds as "x min x sec", omitting whichever part is 0
   */
  formatSecondsAsMinSec(totalSeconds: number): string {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = Math.round(totalSeconds % 60);
    if (minutes > 0 && seconds > 0) {
      return `${minutes} min ${seconds} sec`;
    }
    if (minutes > 0) {
      return `${minutes} min`;
    }
    return `${seconds} sec`;
  }

  onRepsSliderChange(value: number): void {
    this.tempReps = value;
    this.totalRepsChanged.emit(value);
  }

  onToggleVixeyAvailability(): void {
    this.isAvailableToVixey.set(!this.isAvailableToVixey());
    this.hasChanges.set(this.isAvailableToVixey() !== this.originalAvailability());
  }

  onApplyVixeyChange(): void {
    const branchId = this.resolvedBranchId ?? this.branchId;
    if (!branchId || !this.exerciseId || this.isSaving()) {
      return;
    }

    this.isSaving.set(true);
    const newState = this.isAvailableToVixey();

    if (newState) {
      // Toggle is ON - Available to Vixey (not excluded) - DELETE from excluded list
      this.exerciseService.removeFromVixeyExcluded(branchId, this.exerciseId).subscribe({
        next: () => {
          this.originalAvailability.set(true);
          this.hasChanges.set(false);
          this.isSaving.set(false);
          this.changesSaved.set(true);
          // Show success toast
          this.toastService.showSuccess('Exercise is now available to Vixey');
          // Emit event to notify parent that data has changed
          this.vixeyAvailabilityChanged.emit();
          // Close the dialog after saving
          this.onClose();
        },
        error: () => {
          // Revert on error
          this.isAvailableToVixey.set(false);
          this.isSaving.set(false);
          // Show error toast
          this.toastService.showError('Failed to update exercise availability. Please try again.');
        }
      });
    } else {
      // Toggle is OFF - Not available to Vixey (excluded) - POST to excluded list
      this.exerciseService.addToVixeyExcluded({
        exerciseId: this.exerciseId,
        branchId
      }).subscribe({
        next: () => {
          this.originalAvailability.set(false);
          this.hasChanges.set(false);
          this.isSaving.set(false);
          this.changesSaved.set(true);
          // Show success toast
          this.toastService.showSuccess('Exercise is now excluded from Vixey');
          // Emit event to notify parent that data has changed
          this.vixeyAvailabilityChanged.emit();
          // Close the dialog after saving
          this.onClose();
        },
        error: () => {
          // Revert on error
          this.isAvailableToVixey.set(true);
          this.isSaving.set(false);
          // Show error toast
          this.toastService.showError('Failed to update exercise availability. Please try again.');
        }
      });
    }
  }

  onClose(): void {
    // For mobile, set visible to false and let the drawer handle the animation
    if (this.responsiveService.isMobileView()) {
      this.visible.set(false);
      setTimeout(() => {
        this.close.emit();
      }, 300);
    } else {
      this.close.emit();
    }
  }

  onBackdropClick(event: MouseEvent): void {
    // Close only if clicking on the backdrop itself, not the content
    if ((event.target as HTMLElement).classList.contains('dialog-backdrop')) {
      this.onClose();
    }
  }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    this.onClose();
  }

  /**
   * Get level badge color classes
   */
  getLevelClass(level: string): string {
    const classes: Record<string, string> = {
      'Beginner': 'level-beginner',
      'Intermediate': 'level-intermediate',
      'Advanced': 'level-advanced'
    };
    return classes[level] || 'level-default';
  }
}

